import { Component, OnInit, computed, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { getLocaleFromLang } from 'quest-atlas-angular-components';
import { CalendarEntry, CalendarMonth, CardsAdminService } from '../../../services/admin/cards-admin.service';

type CalendarKind = 'rare' | 'legendary';

interface CalendarCell {
  date: string; // ISO date 'YYYY-MM-DD', '' for leading/trailing blanks
  dayNumber: number; // 0 for blanks
  inMonth: boolean;
  isToday: boolean;
  entry: CalendarEntry | null;
}

@Component({
  selector: 'app-cards-calendar-tab',
  imports: [TranslocoModule],
  templateUrl: './cards-calendar-tab.component.html',
  styleUrl: './cards-calendar-tab.component.scss'
})
export class CardsCalendarTabComponent implements OnInit {
  // 'YYYY-MM', defaults to the current month.
  month = signal<string>(this.formatMonthKey(new Date()));
  data = signal<CalendarMonth | null>(null);
  assignKind = signal<CalendarKind>('rare');

  loading = signal(true);
  // True only when the initial GET for the current month failed - distinct from `loading` so the
  // template can tell "still loading" apart from "loading finished, nothing valid to show". Mirrors
  // cards-drops-tab.component.ts's loadFailed guard.
  loadFailed = signal(false);
  // Set on a failed write (POST/DELETE) or on a failed silent reload after a write. Cleared whenever
  // a new month load or a new write starts.
  error = signal<string | null>(null);
  saving = signal(false);

  // Bumped at the start of every loadMonth() call (before the GET is issued) and captured into a
  // local by that call's subscribe handlers. Mirrors cards-drops-tab.component.ts's editSeq guard -
  // loadMonth() fires from ngOnInit, from every prev/next click, and silently from both onDayClick
  // success paths, with no cancellation between them, so HTTP responses are not guaranteed to land in
  // request order. Without this, a slow earlier month's GET (or a slow reload after an earlier write)
  // can resolve after a faster later one and silently overwrite `data` with stale content.
  private requestSeq = 0;

  weeks = computed<CalendarCell[][]>(() => this.buildWeeks(this.month(), this.data()?.entries ?? []));
  monthLabel = computed<string>(() => {
    const [year, monthNum] = this.month().split('-').map(Number);
    const locale = getLocaleFromLang(this.transloco.getActiveLang());
    return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(new Date(year, monthNum - 1, 1));
  });

  constructor(
    private cardsAdmin: CardsAdminService,
    private transloco: TranslocoService
  ) {}

  ngOnInit(): void {
    this.loadMonth();
  }

  // Localized Monday-first weekday initials for the grid header. Not a signal: it has no reactive
  // dependency worth tracking (the active language rarely changes mid-session) and the component
  // uses default change detection, so a plain method re-evaluates on every check same as a computed
  // would - see monthLabel/weeks above for the pattern used where there IS a signal dependency.
  weekdayLabels(): string[] {
    const locale = getLocaleFromLang(this.transloco.getActiveLang());
    const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    // 2024-01-01 was a Monday - walk 7 consecutive days from it to get Mon..Sun labels regardless of
    // which month is currently on screen.
    const labels: string[] = [];
    for (let i = 0; i < 7; i++) {
      labels.push(fmt.format(new Date(2024, 0, 1 + i)));
    }
    return labels;
  }

  setKind(kind: CalendarKind): void {
    this.assignKind.set(kind);
  }

  prevMonth(): void {
    this.shiftMonth(-1);
  }

  nextMonth(): void {
    this.shiftMonth(1);
  }

  onDayClick(cell: CalendarCell): void {
    // Blanks (leading/trailing filler) and cells outside the month carry no date to act on. Also
    // block re-entrancy while a write is already in flight so a double-click cannot fire two writes.
    if (!cell.inMonth || this.saving()) {
      return;
    }
    this.saving.set(true);
    this.error.set(null);

    if (cell.entry) {
      const id = cell.entry.id;
      this.cardsAdmin.deleteCalendar$(id).subscribe({
        next: () => {
          this.saving.set(false);
          this.loadMonth(true);
        },
        error: () => {
          // Without an explicit error callback, a failed DELETE would leave `saving` stuck true
          // forever - the whole grid would look permanently disabled with no way out.
          this.saving.set(false);
          this.error.set('cardsAdminCalendarSaveFailed');
        }
      });
    } else {
      this.cardsAdmin.setCalendarDay$(this.assignKind(), cell.date).subscribe({
        next: () => {
          this.saving.set(false);
          this.loadMonth(true);
        },
        error: (err: HttpErrorResponse) => {
          this.saving.set(false);
          // 409 dayTaken means the other kind already occupies this date - a specific, actionable
          // message instead of the generic save-failed one.
          this.error.set(err.status === 409 && err.error?.error === 'dayTaken' ? 'cardsAdminCalendarDayTaken' : 'cardsAdminCalendarSaveFailed');
        }
      });
    }
  }

  private shiftMonth(delta: number): void {
    const [year, monthNum] = this.month().split('-').map(Number);
    // Built from year/monthIndex/1 per house rule - never string surgery on the month key. The Date
    // constructor normalizes month overflow/underflow itself (e.g. monthIndex -1 rolls back into
    // December of the previous year), so this is safe across year boundaries too.
    const shifted = new Date(year, monthNum - 1 + delta, 1);
    this.month.set(this.formatMonthKey(shifted));
    this.loadMonth();
  }

  // `silent`: reload after a write, without disturbing the grid the admin is actively clicking on.
  // A full loading/loadFailed toggle here would blank the whole grid after every single click; instead
  // the previous month's data (now stale by one entry) stays on screen and only `error` reports a
  // failed refresh, exactly like a failed write would.
  private loadMonth(silent = false): void {
    const seq = ++this.requestSeq;
    if (!silent) {
      this.loading.set(true);
      this.loadFailed.set(false);
      this.error.set(null);
    }
    this.cardsAdmin.getCalendarMonth$(this.month()).subscribe({
      next: (d) => {
        // A slower, earlier month load resolving after a faster, later one (e.g. three quick clicks
        // on `>` on a slow connection, or a silent post-write reload racing a newer one) must not
        // clobber whatever is now on screen. The seq check is the general guard; the `d.month` check
        // is cheap and makes the intent obvious to the next reader, since the payload carries the
        // month it belongs to.
        if (seq !== this.requestSeq || d.month !== this.month()) {
          return;
        }
        this.data.set(d);
        if (!silent) {
          this.loading.set(false);
        }
      },
      error: () => {
        // Same supersede guard as above - a stale failure must not stomp on a newer, still-in-flight
        // (or already-succeeded) request's state.
        if (seq !== this.requestSeq) {
          return;
        }
        // Without this, a failed GET leaves `loading` true forever - the tab is stuck on "Loading..."
        // with no way out short of switching tabs and back.
        if (!silent) {
          this.loading.set(false);
          this.loadFailed.set(true);
        } else {
          this.error.set('cardsAdminCalendarLoadFailed');
        }
      }
    });
  }

  private buildWeeks(monthKey: string, entries: CalendarEntry[]): CalendarCell[][] {
    const [year, monthNum] = monthKey.split('-').map(Number);
    const monthIndex = monthNum - 1;
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
    const firstOfMonth = new Date(year, monthIndex, 1);
    // getDay(): 0=Sunday..6=Saturday. The game's weeks are ISO (Monday-first), so shift to
    // 0=Monday..6=Sunday before using it as a leading-blank count.
    const leadingBlanks = (firstOfMonth.getDay() + 6) % 7;
    const todayKey = this.formatDateKey(new Date());
    const entriesByDay = new Map(entries.map((e) => [e.day, e]));

    const cells: CalendarCell[] = [];
    for (let i = 0; i < leadingBlanks; i++) {
      cells.push(this.blankCell());
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = this.formatDateKey(new Date(year, monthIndex, day));
      cells.push({
        date: dateKey,
        dayNumber: day,
        inMonth: true,
        isToday: dateKey === todayKey,
        entry: entriesByDay.get(dateKey) ?? null
      });
    }
    // Trailing blanks so the last row still has 7 cells.
    while (cells.length % 7 !== 0) {
      cells.push(this.blankCell());
    }

    const weeks: CalendarCell[][] = [];
    for (let i = 0; i < cells.length; i += 7) {
      weeks.push(cells.slice(i, i + 7));
    }
    return weeks;
  }

  private blankCell(): CalendarCell {
    return { date: '', dayNumber: 0, inMonth: false, isToday: false, entry: null };
  }

  private pad2(n: number): string {
    return n < 10 ? `0${n}` : `${n}`;
  }

  // Built from year/monthIndex/1 and formatted with local getters - never toISOString(), which would
  // shift across the UTC boundary and show the wrong month/day for users east of UTC (this app's
  // users are at UTC+3).
  private formatMonthKey(d: Date): string {
    return `${d.getFullYear()}-${this.pad2(d.getMonth() + 1)}`;
  }

  private formatDateKey(d: Date): string {
    return `${d.getFullYear()}-${this.pad2(d.getMonth() + 1)}-${this.pad2(d.getDate())}`;
  }
}
